import { useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarClock, HardHat, PanelTop, PhoneOutgoing } from 'lucide-react'

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '@/components/ui/command'
import { Kbd, KbdGroup } from '@/components/ui/kbd'
import {
  ENTITY_CAPABILITY,
  parseEntityQuery,
  type EntityRef,
  type EntityType,
} from '@/config/entityRoutes'
import { useMyCapabilitySet } from '@/features/permissions/hooks'
import {
  DESTINATION_SECTIONS,
  type Destination,
} from '@/config/navigationDestinations'
import { useAppTabs } from '@/hooks/useAppTabs'
import { useEntityNavigate } from '@/hooks/useEntityNavigate'
import { useCommandPaletteHotkey } from '@/hooks/useCommandPaletteHotkey'
import { useNavigationDestinations } from '@/hooks/useNavigationDestinations'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { getTabIcon } from '@/components/panel/tabBar/tabIcon'
import { matchAppRoute, tabIdFor } from '@/config/routeManifest'
import { useCommandPaletteStore } from '@/stores/commandPaletteStore'

import { OrgSwitchGroup } from './OrgSwitchGroup'
import { PaletteRow, type PaletteRowData } from './PaletteRow'
import { paletteFilter } from './paletteFilter'

/** Workspace-tab id a destination route would open, or null when it opens none. */
function tabIdOfRoute(route: string): string | null {
  const matched = matchAppRoute(route.split('?')[0]!)
  return matched ? tabIdFor(matched.route.routeId, matched.params) : null
}

/** Presentation for a typed entity reference; the routing itself is pure. */
const ENTITY_PRESENTATION: Record<
  EntityType,
  { icon: Destination['icon']; noun: string }
> = {
  call: { icon: PhoneOutgoing, noun: 'call' },
  duty: { icon: HardHat, noun: 'duty' },
  session: { icon: CalendarClock, noun: 'session' },
}

function EntityRow({
  entity,
  onSelect,
}: {
  entity: EntityRef
  onSelect: (entity: EntityRef) => void
}) {
  const { icon: Icon, noun } = ENTITY_PRESENTATION[entity.type]
  return (
    // Force-mounted: the row is built from the query itself, so there is
    // nothing for cmdk's filter to score it against.
    <CommandGroup forceMount heading="Jump to">
      <PaletteRow
        icon={Icon}
        label={`Open ${noun}`}
        value={`entity:${entity.type}:${entity.id}`}
        hint={entity.id}
        forceMount
        onSelect={() => {
          onSelect(entity)
        }}
      />
    </CommandGroup>
  )
}

/**
 * Spotlight over the tab shell. Shift+Space (or ⌘K) anywhere in the app, type
 * a few letters, Enter.
 *
 * Typing `call <id>` or `duty <id>` (or pasting a link to either) offers that
 * record directly, above the name matches. Users who can switch orgs also get
 * their other organizations once they type, so an org's name or slug switches
 * to it.
 *
 * Mount once inside the authenticated layout, outside the tab tree — a dialog
 * rendered inside a tab gets stamped with that tab's route and hidden when the
 * user switches away.
 *
 * Selecting a tab that is already open costs nothing: `ContentPanelAreaInner`
 * keeps every open tab mounted inside a hidden `<Activity>`, so this is a
 * navigate and a reveal, with no remount and no refetch.
 */
export function CommandPalette() {
  // Open/closed lives in a store because the palette is mounted outside the
  // tab tree while its triggers — the hotkey aside — live inside it.
  const open = useCommandPaletteStore((s) => s.open)
  const setPaletteOpen = useCommandPaletteStore((s) => s.setPaletteOpen)
  const togglePalette = useCommandPaletteStore((s) => s.togglePalette)
  const [query, setQuery] = useState('')
  useCommandPaletteHotkey(togglePalette)

  // The flag outlives the component now, so the component has to hand it back.
  // `AuthLayout` renders the palette only in its signed-in branch, so an
  // expiring Clerk session unmounts it — and a sign-in inside the same page
  // load remounts it without reloading the module. Without this reset the
  // dialog would come back up on its own, blank, with nothing having opened
  // it. Under the old `useState` the unmount discarded the flag for free.
  useEffect(
    () => () => {
      setPaletteOpen(false)
    },
    [setPaletteOpen]
  )

  // Clear the query on close so the next Shift+Space opens on a blank slate
  // rather than the last thing that was typed.
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setPaletteOpen(nextOpen)
      if (!nextOpen) setQuery('')
    },
    [setPaletteOpen]
  )

  const capabilities = useMyCapabilitySet()

  // A parsed reference is only offered when the user can actually reach the
  // page behind it. Without this the row is an actionable dead end: `/calls`
  // sits behind `calls.list`, so a member without it would land on the
  // access-denied page. Held back entirely while capabilities are resolving —
  // the same "hold rather than answer" the destinations list applies.
  const entity = useMemo(() => {
    const parsed = parseEntityQuery(query)
    if (!parsed) return null
    const required = ENTITY_CAPABILITY[parsed.type]
    if (!required) return parsed
    if (!capabilities) return null
    return capabilities.has(required) ? parsed : null
  }, [query, capabilities])

  // Same gate as the sidebar's `<OrganizationSwitcher>`. The group also waits
  // for a query: a bare open is for navigating, and mounting is what starts
  // the membership fetch.
  const canSwitchOrg = capabilities?.has('orgSwitcher.switch') === true
  const isSearching = query.trim() !== ''
  const showOrgSwitch = canSwitchOrg && isSearching

  const navigateToEntity = useEntityNavigate()

  const goToEntity = useCallback(
    (ref: EntityRef) => {
      handleOpenChange(false)
      navigateToEntity(ref)
    },
    [handleOpenChange, navigateToEntity]
  )

  const { tabs } = useAppTabs()
  const destinations = useNavigationDestinations()
  const { toTask } = useAppNavigate()

  // Rows are built here rather than in JSX so each one's `keywords` array is
  // stable across renders and PaletteRow isn't handed a fresh array every time.
  // `href` is the tab's own record of the deepest path visited under it, so
  // selecting a tab restores the sub-view rather than its root.
  const openRows = useMemo<PaletteRowData[]>(
    () =>
      tabs.map((tab) => ({
        icon: getTabIcon(tab.routeId) ?? PanelTop,
        label: tab.title,
        // The href is guaranteed unique — two duty tabs can share a title.
        // The title rides in `keywords` so typing it still scores.
        value: tab.href,
        keywords: [tab.title],
        hint: tab.href,
      })),
    [tabs]
  )

  // Anything already open is offered in the first group, so it is never
  // repeated below. Matched on tab identity, not on the path: every duty
  // sub-view shares one tab.
  const groups = useMemo(() => {
    const openTabIds = new Set(tabs.map((tab) => tab.id))
    return DESTINATION_SECTIONS.map((section) => ({
      section,
      items: destinations
        .filter(
          (destination) =>
            destination.section === section &&
            !openTabIds.has(tabIdOfRoute(destination.route) ?? '')
        )
        .map<PaletteRowData>((destination) => ({
          icon: destination.icon,
          label: destination.label,
          value: destination.route,
          keywords: [destination.label, ...(destination.keywords ?? [])],
        })),
    })).filter((group) => group.items.length > 0)
  }, [destinations, tabs])

  // Idle, the palette is a sectioned menu. Searching, it is one ranked list:
  // cmdk sorts rows within a group but not the groups themselves (1.1.1 looks
  // each group up by its id, but the attribute it queries holds the heading,
  // so the lookup never matches). Left sectioned, "tools" would select a weak
  // match in Actions while Tools sat further down under Settings.
  const sections = useMemo(
    () =>
      isSearching
        ? [
            {
              heading: 'Results',
              rows: [...openRows, ...groups.flatMap(({ items }) => items)],
            },
          ]
        : [
            ...(openRows.length > 0
              ? [{ heading: 'Open tabs', rows: openRows }]
              : []),
            ...groups.map(({ section, items }) => ({
              heading: section,
              rows: items,
            })),
          ],
    [isSearching, openRows, groups]
  )

  const go = useCallback(
    (route: string) => {
      handleOpenChange(false)
      toTask(route)
    },
    [handleOpenChange, toTask]
  )

  return (
    <CommandDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Go to"
      description="Jump to any view in Hoku"
      showCloseButton={false}
      filter={paletteFilter}
      className="p-0">
      <CommandInput
        placeholder="Go to… or “call &lt;id&gt;”"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[min(24rem,60vh)]">
        {/* A force-mounted entity row doesn't count towards cmdk's match
            tally, so the empty state has to stand down for it explicitly. */}
        {!entity && <CommandEmpty>No matching view.</CommandEmpty>}

        {entity && <EntityRow entity={entity} onSelect={goToEntity} />}

        {sections.map(({ heading, rows }) => (
          <CommandGroup key={heading} heading={heading}>
            {rows.map((row) => (
              <PaletteRow key={row.value} {...row} onSelect={go} />
            ))}
          </CommandGroup>
        ))}

        {showOrgSwitch && (
          <OrgSwitchGroup onSwitch={() => handleOpenChange(false)} />
        )}
      </CommandList>

      <div className="flex items-center justify-between gap-3 border-t px-3 py-2 text-xs text-muted-foreground">
        <KbdGroup>
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd>
          <span>navigate</span>
          <Kbd>↵</Kbd>
          <span>open</span>
        </KbdGroup>
        <KbdGroup>
          <Kbd>⇧</Kbd>
          <Kbd>space</Kbd>
        </KbdGroup>
      </div>
    </CommandDialog>
  )
}
